import { useState, useEffect } from "react";
import { Menu, X, Phone } from "lucide-react";
import { nav, contact } from "@/config/content";
import { Logo } from "@/components/site/Logo";

const scrollTo = (href) => {
  const el = document.querySelector(href);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    scrollTo(href);
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-[padding,background-color,box-shadow,border-color] duration-300 border-b ${
        scrolled ? "bg-white/95 backdrop-blur-md border-border py-2 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" : "bg-transparent border-transparent py-4"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between gap-4">
        <a href="#start" onClick={(e) => { e.preventDefault(); go("#start"); }} data-testid="logo-link" aria-label="HANSERA Startseite">
          <Logo />
        </a>

        <nav aria-label="Hauptnavigation" className="hidden lg:flex items-center gap-8">
          {nav.map((n) => (
            <button
              key={n.href}
              data-testid={`nav-${n.href.replace("#", "")}`}
              onClick={() => go(n.href)}
              className="text-sm font-medium text-charcoal-muted hover:text-forest transition-colors duration-200 relative after:absolute after:-bottom-1.5 after:left-0 after:h-px after:w-0 after:bg-forest after:transition-[width] after:duration-300 hover:after:w-full"
            >
              {n.label}
            </button>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          {contact.phoneHref && (
            <a href={contact.phoneHref} data-testid="header-phone" className="inline-flex items-center gap-2 text-sm font-medium text-forest hover:text-forest-dark transition-colors">
              <Phone className="w-4 h-4" /> {contact.phone}
            </a>
          )}
          <button
            data-testid="header-cta"
            onClick={() => go("#kontakt")}
            className="inline-flex items-center rounded-md bg-forest px-5 py-2.5 text-sm font-semibold text-white hover:bg-forest-dark hover:-translate-y-0.5 transition-[transform,background-color] duration-200"
          >
            Angebot anfordern
          </button>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden inline-flex items-center justify-center w-11 h-11 rounded-md border border-border bg-white text-forest"
          aria-label={open ? "Menü schließen" : "Menü öffnen"}
          aria-expanded={open}
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-white border-t border-border mt-2">
          <nav aria-label="Mobile Navigation" className="max-w-7xl mx-auto px-4 py-4 flex flex-col gap-1">
            {nav.map((n) => (
              <button key={n.href} onClick={() => go(n.href)} data-testid={`mobile-nav-${n.href.replace("#", "")}`} className="text-left py-3 px-2 text-base font-medium text-charcoal hover:text-forest hover:bg-sand rounded-md transition-colors">
                {n.label}
              </button>
            ))}
            <div className="flex flex-col gap-2 mt-3">
              {contact.phoneHref && (
                <a href={contact.phoneHref} data-testid="mobile-phone" className="inline-flex items-center justify-center gap-2 rounded-md border border-forest px-5 py-3 text-forest font-semibold">
                  <Phone className="w-4 h-4" /> Anrufen
                </a>
              )}
              <button onClick={() => go("#kontakt")} data-testid="mobile-cta" className="inline-flex items-center justify-center rounded-md bg-forest px-5 py-3 text-white font-semibold">
                Angebot anfordern
              </button>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};
